import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import { IconButton, Popper, Paper, MenuList, MenuItem, ClickAwayListener, CircularProgress, Dialog } from '@material-ui/core';
import { VideoLibrary, PhotoLibrary, ArrowDropDown, PhoneAndroid, Videocam } from '@material-ui/icons';
import { Text } from 'Components/text';
import { ReactComponent as VideoUploadIcon } from 'Assets/video-upload.svg';
import ForNoImage from 'Assets/Placeholder.png';
import { v4 as uuid } from 'uuid';
import AXIOS from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { saveUploadInfo, saveUploadDetails, saveDummy, removeDummy } from 'store/store';
import { AppContext } from 'Context';
import { uploadLargeFile } from 'Utils/uploadWrapper';
import { getBucketName, generateS3Url } from 'Api/awsConfig';
import { axios, baseUrl } from 'Api';
import { Pathname } from 'Routes';
import { ALLOWED_UPLOADS_COUNT, MAX_PHOTOS_PER_UPLOAD } from 'Commons';
import uploadManager from 'Utils/uploadManagerSecure';
import { SHARED_FEATURE_ENABLED } from 'Config/featureFlags';
import { logAppEvent } from '../../Api/errorLogger';
import CloudUploadModal from './CloudUploadModal';
import CloudPhotoAlbumModal from './CloudPhotoAlbumModal';
import MobileUploadQRModal from './MobileUploadQRModal';
import UploadInProgressModal from '../UploadInProgressModal';
import UploadLinkModal from './UploadLinkModal';
import styles from './UploadDropdown.module.css';
// Cap-popup styling lives with the photo modal so both popups stay identical.
import photoModalStyles from '../PhotoUploadModal/PhotoUploadModal.module.css';

// Local-file picker support (Choose Local Files tile) — mirrors /storage/videos
const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.avi', '.mkv', '.wmv', '.flv', '.webm', '.m4v', '.3gp', '.mpg', '.mpeg', '.ts', '.mts'];
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.heic', '.heif', '.tiff', '.tif', '.svg'];
const FILE_ACCEPT = `${[...VIDEO_EXTENSIONS, ...IMAGE_EXTENSIONS].join(',')},video/*,image/*`;

// Brand glyphs shared between the dropdown menu items and the grid variant.
const ApplePhotosGlyph = ({ className }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
  >
    <path d="M17.05 20.28c-.98.95-2.05.79-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

const GooglePhotosGlyph = ({ className }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 122.88 122.88"
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
  >
    <path d="M30.72,27.97c16.95,0,30.72,13.77,30.72,30.72v2.83H2.83c-1.52,0-2.75-1.23-2.83-2.83 C0,41.73,13.77,27.97,30.72,27.97L30.72,27.97z" />
    <path d="M94.91,30.72c0,16.95-13.77,30.72-30.72,30.72l-2.75,0V2.83C61.44,1.3,62.67,0,64.19,0 C81.22,0,94.91,13.77,94.91,30.72L94.91,30.72z" />
    <path d="M92.16,94.99c-16.95,0-30.72-13.77-30.72-30.72v-2.83h58.61c1.52,0,2.83,1.23,2.83,2.75 C122.88,81.22,109.11,94.99,92.16,94.99L92.16,94.99L92.16,94.99z" />
    <path d="M27.97,92.16c0-16.95,13.77-30.72,30.72-30.72h2.75v58.61c0,1.52-1.23,2.75-2.83,2.83 C41.66,122.88,27.97,109.11,27.97,92.16L27.97,92.16L27.97,92.16z" />
  </svg>
);

const DropboxGlyph = ({ className }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
  >
    <path d="M6 1.807L0 5.629l6 3.822 6.001-3.822L6 1.807zM18 1.807l-6 3.822 6 3.822 6-3.822-6-3.822zM0 13.274l6 3.822 6.001-3.822L6 9.452l-6 3.822zM18 9.452l-6 3.822 6 3.822 6-3.822-6-3.822zM6 18.371l6.001 3.822 6-3.822-6-3.822L6 18.371z" />
  </svg>
);

const GoogleDriveGlyph = ({ className }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 87.3 78"
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
  >
    <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" />
    <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" />
    <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" />
    <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" />
    <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" />
    <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" />
  </svg>
);

// ── Grid-variant icons — matched 1:1 to the /storage/videos upload tiles ──
// Videos & Photos share this "local files" glyph (per product spec).
const LocalFilesGridIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="#02071A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 2V8H20" stroke="#02071A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 18V12" stroke="#02071A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 15L12 12L15 15" stroke="#02071A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MobileGridIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="5" y="1" width="14" height="22" rx="3" stroke="#02071A" strokeWidth="1.5" />
    <line x1="10" y1="3.5" x2="14" y2="3.5" stroke="#02071A" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="12" cy="19" r="1" fill="#02071A" />
  </svg>
);

const StoryGridIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="6" width="14" height="12" rx="2" stroke="#02071A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M22 8L16 12L22 16V8Z" stroke="#02071A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="9" cy="12" r="2.6" fill="#ff4d4f" />
  </svg>
);

const ApplePhotosGridIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.05 20.28c-.98.95-2.05.79-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" fill="#555" />
  </svg>
);

const GooglePhotosGridIcon = () => (
  <svg width="40" height="40" viewBox="0 0 122.88 122.88" xmlns="http://www.w3.org/2000/svg">
    <path d="M30.72,27.97c16.95,0,30.72,13.77,30.72,30.72v2.83H2.83c-1.52,0-2.75-1.23-2.83-2.83 C0,41.73,13.77,27.97,30.72,27.97L30.72,27.97z" fill="#F6B704" />
    <path d="M94.91,30.72c0,16.95-13.77,30.72-30.72,30.72l-2.75,0V2.83C61.44,1.3,62.67,0,64.19,0 C81.22,0,94.91,13.77,94.91,30.72L94.91,30.72z" fill="#E54335" />
    <path d="M92.16,94.99c-16.95,0-30.72-13.77-30.72-30.72v-2.83h58.61c1.52,0,2.83,1.23,2.83,2.75 C122.88,81.22,109.11,94.99,92.16,94.99L92.16,94.99L92.16,94.99z" fill="#4280EF" />
    <path d="M27.97,92.16c0-16.95,13.77-30.72,30.72-30.72h2.75v58.61c0,1.52-1.23,2.75-2.83,2.83 C41.66,122.88,27.97,109.11,27.97,92.16L27.97,92.16L27.97,92.16z" fill="#34A353" />
  </svg>
);

const DropboxGridIcon = () => (
  <svg width="40" height="40" viewBox="0 0 43 40" xmlns="http://www.w3.org/2000/svg">
    <path d="M12.5 0L0 8.1l8.6 6.9 12.5-7.8L12.5 0zM0 21.9l12.5 8.1 8.6-7.1-12.5-7.8L0 21.9zM21.1 22.9l8.6 7.1 12.5-8.1-8.6-6.8-12.5 7.8zM42.2 8.1L29.7 0l-8.6 7.2 12.5 7.8 8.6-6.9zM21.2 24.6l-8.6 7.1-4 -2.6v2.9l12.6 7.6 12.6-7.6v-2.9l-4 2.6-8.6-7.1z" fill="#0061ff" />
  </svg>
);

const GoogleDriveGridIcon = () => (
  <svg width="32" height="32" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5l5.4 9.35z" fill="#0066DA" />
    <path d="M43.65 25L29.9 1.2c-1.35.8-2.5 1.9-3.3 3.3L1.2 52.35c-.8 1.4-1.2 2.95-1.2 4.5h27.5L43.65 25z" fill="#00AC47" />
    <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75L86.1 57c.8-1.4 1.2-2.95 1.2-4.5H59.85L73.55 76.8z" fill="#EA4335" />
    <path d="M43.65 25L57.4 1.2c-1.35-.8-2.9-1.2-4.5-1.2H34.35c-1.6 0-3.15.45-4.5 1.2L43.65 25z" fill="#00832D" />
    <path d="M59.85 53H27.5l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.55c1.6 0 3.15-.45 4.5-1.2L59.85 53z" fill="#2684FC" />
    <path d="M73.4 26.5l-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25l16.2 28h27.5c0-1.55-.4-3.1-1.2-4.5l-12.75-22z" fill="#FFBA00" />
  </svg>
);

const UploadDropdown = ({
  onVideoUpload,
  onPhotoUpload,
  disabled = false,
  className = '',
  isCompact = false,
  isLoading = false,
  onCloudFilesSelected,
  showCloudOptions = false,
  variant = 'button'
}) => {
  const {
    userDetails,
    setUploadContainer,
    setUploadContainer2,
    setIamUploading,
    setDraftCategoryState,
    setAppSnackbar,
    base64ToBinary,
    setDownloadBox,
    setDownloadingFiles,
    autoSignIn,
    setTrailPopUpModalVisible,
    IsfreePlan,
    downloadAbortControllersRef,
    bulkDownloadCancelRef,
    // Cloud photo album modal state from context (persists across navigation)
    photoAlbumModalOpen,
    setPhotoAlbumModalOpen,
    pendingCloudPhotos,
    setPendingCloudPhotos,
    pendingCloudPhotoService,
    setPendingCloudPhotoService,
  } = useContext(AppContext);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userId = userDetails?.isSubscription?.user_id || localStorage.getItem('id');

  const uploadPercentages = useSelector((state) => state.upload);
  const updateUploadPercentages = (draft_id, data) => {
    dispatch(saveUploadInfo({ [draft_id]: data }));
  };
  const updateUploadDetails = (draft_id, data) => {
    dispatch(saveUploadDetails({ [draft_id]: data }));
  };

  const updateDummy = (dummy, status = 0) => {
    if (status === 1) {
      dispatch(saveDummy({ [dummy?.draft_id]: dummy }));
    } else {
      dispatch(removeDummy(dummy));
    }
  };

  // Helper functions
  function changeSize(size) {
    const newSize = size / 1024 / 1024;
    return newSize.toFixed(2);
  }

  // Helper functions for thumbnail generation
  const blobToBase64 = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  const convertBlobUrlToBase64 = async (blobUrl) => {
    try {
      const response = await fetch(blobUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch blob');
      }
      const blob = await response.blob();
      const base64 = await blobToBase64(blob);
      return base64;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error converting blob to base64:', error);
      throw error;
    }
  };

  const generateThumbnailsFromVideo = (file) => new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('No file provided'));
      return;
    }

    const video = document.createElement('video');
    const videoUrl = URL.createObjectURL(file);
    video.src = videoUrl;
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;

    video.onerror = () => {
      URL.revokeObjectURL(videoUrl);
      video.remove();
      // Return ForNoImage as fallback
      resolve({
        thumbnails: [ForNoImage],
        videoHeight: 0,
        videoWidth: 0,
        videoDuration: 0,
      });
    };

    video.onloadeddata = () => {
      const { duration, videoHeight, videoWidth } = video;

      const thumbnailCount = 6;
      const interval = duration / (thumbnailCount + 1);
      const tempThumbnails = [];

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      let currentIndex = 1;

      const captureNextFrame = () => {
        if (currentIndex > thumbnailCount) {
          URL.revokeObjectURL(videoUrl);
          video.remove();

          if (tempThumbnails.length === 0) {
            resolve({
              thumbnails: [ForNoImage],
              videoHeight,
              videoWidth,
              videoDuration: duration,
            });
          } else {
            resolve({
              thumbnails: tempThumbnails,
              videoHeight,
              videoWidth,
              videoDuration: duration,
            });
          }
          return;
        }

        const time = interval * currentIndex;
        video.currentTime = time;

        video.onseeked = () => {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const url = URL.createObjectURL(blob);
                tempThumbnails.push(url);
              }

              currentIndex += 1;
              captureNextFrame();
            },
            'image/jpeg',
            0.9
          );
        };
      };

      captureNextFrame();
    };
  });

  const addVideoToAPI = async (draftID, draftCategoryState) => {
    try {
      const apiUrl = `${baseUrl}/V6/addVideo`;
      const formData = new FormData();
      const draftData = draftCategoryState[draftID];
      if (draftData) {
        formData.append('draft_id', draftID || '');
        formData.append('title', draftData.title || '');
        formData.append('category_id', draftData.category || '');
        if (draftData.category_name) {
          formData.append('category_name', draftData.category_name);
        }
        formData.append('file_name', draftData.fileName || '');
        if (draftData.thumbnail) {
          const binaryThumbnail = base64ToBinary(draftData.thumbnail);
          formData.append('thumbnail', binaryThumbnail);
        }
        // Append each thumbnail as binary
        if (draftData.thumbnails && Array.isArray(draftData.thumbnails)) {
          draftData.thumbnails.forEach((binaryThumbnail) => {
            if (binaryThumbnail) {
              formData.append('thumbnails[]', binaryThumbnail);
            }
          });
        }
        formData.append('description', draftData.description || '');
        formData.append('video_size', changeSize(draftData.videoSize) || '');
        formData.append('group_ids', JSON.stringify(draftData.selectedGroups || []));
        formData.append('visibility', draftData.visibility || '');
        formData.append('subcategory_id', draftData.selectedSubCategory || '');
        formData.append('playlist_ids', draftData.selectedPlaylist || '');
        formData.append('videoHeight', draftData.videoHeight || '');
        formData.append('videoWidth', draftData.videoWidth || '');
        formData.append('videoOreintation', draftData.orientation || '');
        formData.append('s3_status', draftData.uploadStatus || '');
        formData.append('uploadKey', draftData.uploadKey || '');
        formData.append(
          'bucket_folder_path',
          `${draftData.s3Url}${draftData.uploadBucket}/${draftData.uploadKey}`
        );
        formData.append('s3_image_bucket_url', draftData.s3Url || '');
      }
      const response = await axios.post(apiUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error adding video to API:', error.message || error);
      throw error;
    }
  };

  const [open, setOpen] = useState(false);
  const [cloudModalOpen, setCloudModalOpen] = useState(false);
  const [selectedCloudService, setSelectedCloudService] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [mobileUploadModalOpen, setMobileUploadModalOpen] = useState(false);
  // Shown when the user tries to start a mobile / Apple Photos upload while
  // another upload is still running.
  const [uploadInProgressOpen, setUploadInProgressOpen] = useState(false);
  const [uploadLinkModalOpen, setUploadLinkModalOpen] = useState(false);
  const anchorRef = useRef(null);

  // Photo album modal state - now using context (photoAlbumModalOpen, pendingCloudPhotos, pendingCloudPhotoService)

  // { attempted, added } when a local pick overshot MAX_PHOTOS_PER_UPLOAD — drives the cap popup.
  const [photoCapNotice, setPhotoCapNotice] = useState(null);
  // to persist across navigation

  // Track completed cloud uploads for cancel toast
  const cloudUploadCompletedCountRef = useRef(0);
  const cloudUploadTotalCountRef = useRef(0);
  const isCloudUploadSessionActiveRef = useRef(false);

  // Check if we should auto-open cloud modal after OAuth
  useEffect(() => {
    const autoOpenService = sessionStorage.getItem('autoOpenCloudModal');
    if (autoOpenService) {
      sessionStorage.removeItem('autoOpenCloudModal');
      setSelectedCloudService(autoOpenService);
      setCloudModalOpen(true);
    }
  }, []);

  // Helper to show cloud upload cancel toast (only once per session)
  const showCloudUploadCancelToast = useCallback(() => {
    if (isCloudUploadSessionActiveRef.current) {
      const completedCount = cloudUploadCompletedCountRef.current;
      setAppSnackbar({
        isVisible: true,
        type: 'success',
        message: `Upload cancelled. ${completedCount} ${completedCount === 1 ? 'item' : 'items'} uploaded`,
      });
      // Reset session flag to prevent duplicate toasts
      isCloudUploadSessionActiveRef.current = false;
    }
  }, [setAppSnackbar]);

  const handleToggle = () => {
    if (!disabled) {
      setOpen((prevOpen) => !prevOpen);
    }
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleVideoUpload = async (event) => {
    // console.log('[UPLOAD_FLOW] Step 4: User clicked "Upload Videos" in dropdown menu');
    handleClose(event);
    if (onVideoUpload) {
      // console.log('[UPLOAD_FLOW] Step 5: Calling onVideoUpload callback to open VideoUploadModal');
      await onVideoUpload();
    }
  };

  const handlePhotoUpload = async (event) => {
    handleClose(event);
    if (onPhotoUpload) {
      await onPhotoUpload();
    }
  };

  const SectionTitle = ({ title, action: Action }) => (
  <div className={styles.sectionTitle}>
    <Text.SectionTitle>{title}</Text.SectionTitle>
    {Action && <Action />}
  </div>
);

  // Tell Your Story — sends the user to /gift-content with
  // autoRecord=true&fromNormalApp=true, which auto-pops the
  // PersonalVideoMessageFlow ("Tell Your Story") overlay. Recorded clips upload
  // through gift-content's direct addVideo pipeline (which handles
  // MediaRecorder's webm output correctly, unlike the standard upload modal),
  // and fromNormalApp makes it return the user to /your-videos once they finish.
  const handleRecordYourself = async (event) => {
    handleClose(event);

    // Free-plan limit check — same as the other upload options. A free user at
    // the item cap gets the upgrade ("buy") popup instead of the recorder.
    try {
      const res = await autoSignIn();
      const currentlyUploadingCount = uploadManager.getCurrentlyUploadingCount();
      if (
        IsfreePlan
        && !res?.data?.subscription
        && res?.data?.totalContentUploaded + currentlyUploadingCount >= ALLOWED_UPLOADS_COUNT
      ) {
        setTrailPopUpModalVisible(true);
        return;
      }
    } catch (error) {
      // If the check fails, don't block the user — fall through to the recorder.
      console.error('Error checking upload limits (Tell Your Story):', error);
    }

    const base = Pathname.giftFlow.content;
    const sep = base.includes('?') ? '&' : '?';
    navigate(`${base}${sep}autoRecord=true&fromNormalApp=true`);
  };

  // Open the mobile-upload QR flow — but if an upload is already running, block
  // it and show the "upload in progress" popup instead. Used by both "Upload
  // From Phone" and "Apple Photos" (Apple Photos has no dedicated integration
  // and routes to the same mobile QR flow, matching the uploader UI).
  const openMobileUploadGuarded = (event) => {
    handleClose(event);
    if (uploadManager.getCurrentlyUploadingCount() > 0) {
      setUploadInProgressOpen(true);
      return;
    }
    setMobileUploadModalOpen(true);
  };
  const handleApplePhotos = openMobileUploadGuarded;
  const handleUploadViaLink = (event) => {
    handleClose(event);
    setUploadLinkModalOpen(true);
  };

  const handleCloudUpload = (service) => async (event) => {
    handleClose(event);

    // Check upload limit before opening cloud modal
    try {
      const res = await autoSignIn();
      const currentlyUploadingCount = uploadManager.getCurrentlyUploadingCount();

      // Check if user has reached upload limit
      if (
        IsfreePlan
        && !res?.data?.subscription
        && res?.data?.totalContentUploaded + currentlyUploadingCount >= ALLOWED_UPLOADS_COUNT
      ) {
        // Show limit modal instead of cloud browser
        setTrailPopUpModalVisible(true);
        return;
      }

      // If limit not reached, open cloud browser
      setSelectedCloudService(service);
      setCloudModalOpen(true);
    } catch (error) {
      console.error('Error checking upload limits:', error);
      // Still open cloud browser if check fails
      setSelectedCloudService(service);
      setCloudModalOpen(true);
    }
  };

  const handleCloudModalClose = () => {
    setCloudModalOpen(false);
    setSelectedCloudService(null);
  };

  const handleCloudFilesSelected = async (files, service) => {
    if (files.length === 0) return;

    // Separate videos and photos based on fileType property
    const videoFiles = files.filter((file) => file.fileType === 'video');
    const photoFiles = files.filter((file) => file.fileType === 'image');

    // eslint-disable-next-line no-console
    console.log('[CLOUD_UPLOAD] Files separated:', { videos: videoFiles.length, photos: photoFiles.length });

    // If there are photos, store them and show album selection modal
    if (photoFiles.length > 0) {
      setPendingCloudPhotos(photoFiles);
      setPendingCloudPhotoService(service);
      setPhotoAlbumModalOpen(true);
    }

    // If no videos, just handle photos (modal will process them)
    if (videoFiles.length === 0) {
      handleCloudModalClose();
      return;
    }

    // Process videos through the existing flow
    setIsUploading(true);
    const filesToProcess = videoFiles;

    // Reset cancel flag for new cloud download session
    if (bulkDownloadCancelRef) {
      bulkDownloadCancelRef.current = false;
    }

    // Reset cloud upload tracking for this session
    cloudUploadCompletedCountRef.current = 0;
    cloudUploadTotalCountRef.current = filesToProcess.length;
    isCloudUploadSessionActiveRef.current = true;

    // Initialize download progress box - mark first 2 files as downloading, rest as queued
    const MAX_CONCURRENT_DOWNLOADS = 2;
    setDownloadBox(true);
    const initialDownloadingFiles = filesToProcess.map((file, index) => ({
      id: file.id,
      name: file.name,
      progress: 0,
      status: index < MAX_CONCURRENT_DOWNLOADS ? 'downloading' : 'queued',
      source: 'cloud', // Mark as cloud download for identification
    }));
    setDownloadingFiles(initialDownloadingFiles);

    try {
      // Get user's cloud service access token from localStorage
      const cloudServices = localStorage.getItem('cloudConnectedServices');
      const cloudAccessToken = cloudServices ? JSON.parse(cloudServices)[service]?.accessToken : null;

      if (!cloudAccessToken) {
        throw new Error('Cloud service not connected');
      }

      // Process each file: download from cloud and upload to S3
      // Downloads happen sequentially (one at a time), uploads are queued by uploadManager
      const processFile = async (file) => {
        // Check if cancelled before starting
        if (bulkDownloadCancelRef?.current) {
          setDownloadingFiles((prev) => prev.map((f) => (f.id === file.id ? { ...f, status: 'cancelled' } : f)));
          showCloudUploadCancelToast();
          return { success: false, fileName: file.name, cancelled: true };
        }

        // Create AbortController for this download
        const controller = new AbortController();
        if (downloadAbortControllersRef) {
          downloadAbortControllersRef.current.set(file.id, controller);
        }

        // Create draft ID early for download tracking
        const draftID = uuid();
        const videoTitle = file.name.replace(/\.[^/.]+$/, '');

        try {
          // Create dummy entry to show download in upload panel
          const downloadDummy = {
            draft_id: draftID,
            title: videoTitle,
            fileName: file.name,
            thumbnail: ForNoImage, // Use placeholder until actual thumbnail is generated
            category: `${service === 'dropbox' ? 'Dropbox' : 'Google Drive'}`,
            video_description: '',
            video_size: changeSize(file.size),
            type: 0, // Content type (0 = video)
            group_id: [],
            content_status: 'Video',
            uploadIsTrying: 1, // Flag indicating upload in progress
          };

          // Add to upload panel before download starts
          updateDummy(downloadDummy, 1);
          updateUploadPercentages(draftID, 'Downloading...');

          let tempUrl = null;
          let headers = {};

          if (service === 'dropbox') {
            // Get Dropbox temporary link
            const dropboxHeaders = {
              Authorization: `Bearer ${cloudAccessToken}`,
              'Content-Type': 'application/json',
            };

            // Add namespace header for shared folder access
            if (file.namespaceId) {
              dropboxHeaders['Dropbox-API-Path-Root'] = JSON.stringify({
                '.tag': 'namespace_id',
                namespace_id: file.namespaceId,
              });
            }

            const response = await AXIOS.post(
              'https://api.dropboxapi.com/2/files/get_temporary_link',
              { path: file.id },
              { headers: dropboxHeaders },
            );
            tempUrl = response.data.link;
          } else if (service === 'googledrive') {
            // Google Drive direct download link
            tempUrl = `https://www.googleapis.com/drive/v3/files/${file.id}?alt=media`;
            headers = {
              Authorization: `Bearer ${cloudAccessToken}`,
            };
          }

          // Download file with progress tracking and abort signal
          const downloadResponse = await fetch(tempUrl, { headers, signal: controller.signal });

          if (!downloadResponse.ok) {
            throw new Error(`Failed to download ${file.name}`);
          }

          const contentLength = downloadResponse.headers.get('content-length');
          // Use content-length header, or file.size from cloud API, or 0 if unknown
          const totalBytes = contentLength ? parseInt(contentLength, 10) : (file.size || 0);

          // Read response body with progress tracking
          const reader = downloadResponse.body.getReader();
          const chunks = [];
          let receivedBytes = 0;
          let lastProgressUpdate = 0;
          let lastSpeedUpdate = 0;
          let lastBytesForSpeed = 0;
          let currentSpeed = 0;

          // eslint-disable-next-line no-constant-condition
          while (true) {
            // Check if cancelled during download
            if (bulkDownloadCancelRef?.current || controller.signal.aborted) {
              reader.cancel();
              throw new Error('Download cancelled');
            }

            // eslint-disable-next-line no-await-in-loop
            const { done, value } = await reader.read();

            if (done) break;

            chunks.push(value);
            receivedBytes += value.length;

            // Throttle progress updates to every 100ms to avoid too many state updates
            const now = Date.now();
            if (now - lastProgressUpdate > 100) {
              lastProgressUpdate = now;

              // Calculate speed every 500ms for stable display
              if (now - lastSpeedUpdate > 500) {
                const bytesDownloaded = receivedBytes - lastBytesForSpeed;
                const timeDiff = (now - lastSpeedUpdate) / 1000; // seconds
                currentSpeed = timeDiff > 0 ? bytesDownloaded / timeDiff : 0;
                lastSpeedUpdate = now;
                lastBytesForSpeed = receivedBytes;
              }

              // Calculate progress and ETA
              const currentReceivedBytes = receivedBytes;
              const speedSnapshot = currentSpeed;
              let downloadProgress;
              let etaValue = 0;

              if (totalBytes > 0) {
                downloadProgress = Math.round((currentReceivedBytes / totalBytes) * 100);
                // Calculate ETA
                if (speedSnapshot > 0) {
                  const remainingBytes = totalBytes - currentReceivedBytes;
                  etaValue = Math.round(remainingBytes / speedSnapshot);
                }
              } else {
                downloadProgress = 0; // Show 0% when size unknown, UI will show speed/eta
              }

              updateUploadPercentages(draftID, `Downloading: ${downloadProgress}%`);

              // Update download progress box with speed and eta
              setDownloadingFiles((prev) => prev.map((f) => (f.id === file.id ? {
                ...f,
                progress: downloadProgress,
                receivedBytes: currentReceivedBytes,
                speed: speedSnapshot,
                eta: etaValue,
              } : f)));
            }
          }

          // Combine chunks into blob
          const blob = new Blob(chunks);

          // Mark download as completed in progress box
          setDownloadingFiles((prev) => prev.map((f) => (f.id === file.id ? { ...f, progress: 100, status: 'completed' } : f)));

          // Remove download dummy - upload will create its own entry
          updateDummy(downloadDummy, 0);

          // Create a File object from blob (like local file upload)
          const fileObject = new File([blob], file.name, {
            type: file.mimeType || blob.type || 'application/octet-stream',
          });

          // Show thumbnail generation status
          updateUploadPercentages(draftID, 'Generating thumbnails...');

          // Generate thumbnails from video
          let thumbnailData;
          try {
            thumbnailData = await generateThumbnailsFromVideo(fileObject);
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error generating thumbnails:', error);
            // Use fallback
            thumbnailData = {
              thumbnails: [ForNoImage],
              videoHeight: 0,
              videoWidth: 0,
              videoDuration: 0,
            };
          }

          const { thumbnails, videoHeight, videoWidth } = thumbnailData;

          // Get main thumbnail (first one)
          let thumbnailBase64 = '';
          const selectedThumbnailSrc = thumbnails[0];

          if (selectedThumbnailSrc === ForNoImage) {
            // Convert ForNoImage to base64
            const response = await fetch(ForNoImage);
            const thumbnailBlob = await response.blob();
            thumbnailBase64 = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.readAsDataURL(thumbnailBlob);
            });
          } else if (selectedThumbnailSrc && selectedThumbnailSrc.startsWith('blob:')) {
            try {
              thumbnailBase64 = await convertBlobUrlToBase64(selectedThumbnailSrc);
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error('Error converting thumbnail:', error);
              // Fallback to ForNoImage
              const response = await fetch(ForNoImage);
              const thumbnailBlob = await response.blob();
              thumbnailBase64 = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.readAsDataURL(thumbnailBlob);
              });
            }
          }

          // Convert all thumbnails to binary
          const thumbnailsBinary = await Promise.all(
            thumbnails.map(async (thumbnail) => {
              try {
                if (thumbnail === ForNoImage) {
                  const response = await fetch(ForNoImage);
                  const thumbnailBlob = await response.blob();
                  const base64 = await new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.readAsDataURL(thumbnailBlob);
                  });
                  return base64ToBinary(base64);
                }

                if (!thumbnail) {
                  return null;
                }
                const base64 = await convertBlobUrlToBase64(thumbnail);
                return base64ToBinary(base64);
              } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error converting thumbnail:', error);
                return null;
              }
            })
          );

          // Generate unique filename and get S3 config (same as VideoUploadModal)
          const fileExtension = fileObject.name.split('.').pop().toLowerCase();
          const number = Math.floor(Math.random() * 10000) + 1;
          const fileName = `${Date.now()}-${userId}-video-${number}.${fileExtension}`;
          const bucketName = getBucketName(userId);
          const s3Url = generateS3Url();

          // Upload to S3 immediately after download (uploadLargeFile handles all tracking)
          uploadLargeFile({
            bucket: bucketName,
            key: fileName,
            file: fileObject,
            draftId: draftID,
            uploadPercentages,
            updateUploadPercentages,
            setUploadContainer,
            setUploadContainer2,
            setIamUploading,
            dummyData: {
              title: videoTitle,
              fileName,
              thumbnail: thumbnailBase64,
              thumbnails: thumbnailsBinary,
              ForNoImage,
              category: '',
              videoSize: fileObject.size,
              changeSize,
            },
            updateDummy, // Use actual updateDummy function to show in upload panel
            setDraftCategoryState,
            onSuccess: async (draft_id, secureUploadData) => {
              // Increment completed cloud upload count
              cloudUploadCompletedCountRef.current += 1;

              // Use secure upload data if available (new flow), otherwise fall back to old format
              const isSecureUpload = secureUploadData && secureUploadData.s3Key;

              // Save minimal draft state for API call
              const draftData = {
                title: videoTitle,
                // category_name: file.folder_name || '', // Pass folder name to category_name for addVideo API
                category_name: 'All Videos',
                fileName,
                thumbnail: thumbnailBase64,
                thumbnails: isSecureUpload ? secureUploadData.thumbnails : thumbnailsBinary,
                description: '',
                videoSize: fileObject.size,
                selectedGroups: [],
                visibility: '2', // All Viewers
                selectedSubCategory: '',
                selectedPlaylist: '',
                videoHeight,
                videoWidth,
                orientation: videoHeight > videoWidth ? 2 : 1,
                uploadStatus: 'completed',
                // Use secure upload S3 key if available, otherwise use old format
                uploadKey: isSecureUpload ? secureUploadData.s3Key : fileName,
                uploadBucket: isSecureUpload ? '' : bucketName, // No bucket prefix for secure uploads
                s3Url: isSecureUpload ? '' : s3Url, // No s3Url for secure uploads
                documentId: isSecureUpload ? secureUploadData.documentId : undefined,
              };

              setDraftCategoryState((prevState) => ({
                ...prevState,
                [draft_id]: draftData,
              }));

              // Create a temporary draftCategoryState object for the API call
              const tempDraftState = {
                [draft_id]: draftData,
              };

              // Call API to save video metadata
              try {
                await addVideoToAPI(draft_id, tempDraftState);
                // setAppSnackbar({
                //   isVisible: true,
                //   message: `Successfully uploaded ${file.name} from ${service === 'dropbox' ? 'Dropbox' : 'Google Drive'}`,
                //   type: 'success',
                // });
              } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Failed to add video to API:', error);
                logAppEvent({
                  userName: `${userDetails?.firstname || localStorage.getItem('firstname') || ''} ${userDetails?.lastname || localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
                  functionAffected: 'Cloud Download',
                  description: `Cloud video downloaded successfully but addVideo API failed — file: "${file.name}", service: ${service}`,
                  severity: 'high',
                  errorMessage: error?.message || 'addVideo API failed after cloud download',
                  component: 'UploadDropdown',
                  userAction: 'Cloud video download called',
                });
                setAppSnackbar({
                  isVisible: true,
                  message: `Uploaded ${file.name} but failed to save metadata`,
                  type: 'warning',
                });
              }
            },
            onError: (error) => {
              // eslint-disable-next-line no-console
              console.error('Upload failed for:', file.name, error);
              logAppEvent({
                userName: `${userDetails?.firstname || localStorage.getItem('firstname') || ''} ${userDetails?.lastname || localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
                functionAffected: 'Cloud Download',
                description: `Cloud video S3 upload failed after download — file: "${file.name}", service: ${service}`,
                severity: 'high',
                errorMessage: error?.message || 'S3 upload failed after cloud download',
                component: 'UploadDropdown',
                userAction: 'Cloud video download called',
              });
              setAppSnackbar({
                isVisible: true,
                message: `Failed to upload ${file.name}`,
                type: 'error',
              });
            },
            updateUploadDetails,
          });

          // Clean up abort controller on success
          if (downloadAbortControllersRef) {
            downloadAbortControllersRef.current.delete(file.id);
          }

          return { success: true, fileName: file.name };
        } catch (error) {
          // Clean up abort controller
          if (downloadAbortControllersRef) {
            downloadAbortControllersRef.current.delete(file.id);
          }

          // Check if this was a cancellation
          const isCancelled = error.name === 'AbortError' || error.message === 'Download cancelled' || bulkDownloadCancelRef?.current;

          if (isCancelled) {
            // Mark as cancelled, don't show error
            setDownloadingFiles((prev) => prev.map((f) => (f.id === file.id ? { ...f, status: 'cancelled' } : f)));
            updateUploadPercentages(draftID, null);
            updateDummy({ draft_id: draftID }, 0);
            showCloudUploadCancelToast();
            return { success: false, fileName: file.name, cancelled: true };
          }

          // eslint-disable-next-line no-console
          console.error(`Error processing ${file.name}:`, error);

          // Mark download as error in progress box
          setDownloadingFiles((prev) => prev.map((f) => (f.id === file.id ? { ...f, status: 'error' } : f)));

          // Clean up dummy entry on error
          updateUploadPercentages(draftID, null);
          updateDummy({ draft_id: draftID }, 0);

          logAppEvent({
            userName: `${userDetails?.firstname || localStorage.getItem('firstname') || ''} ${userDetails?.lastname || localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
            functionAffected: 'Cloud Download',
            description: `Cloud video download failed — file: "${file.name}", service: ${service}`,
            severity: 'high',
            errorMessage: error?.message || 'Cloud video download failed',
            component: 'UploadDropdown',
            userAction: 'Cloud video download called',
          });

          setAppSnackbar({
            isVisible: true,
            message: `Failed to download ${file.name}: ${error.message}`,
            type: 'error',
          });
          return { success: false, fileName: file.name, error: error.message };
        }
      };

      // Process downloads with concurrency of 2 (like "All videos download" feature)
      // When one download completes, the next queued item takes its place
      // Uploads are handled by uploadManager which has its own queue
      const MAX_CONCURRENT_DOWNLOADS_LIMIT = 2;
      const downloadState = { currentIndex: 0, activeDownloads: 0 };

      const startNextDownload = () => {
        // Check if cancelled - don't start new downloads
        if (bulkDownloadCancelRef?.current) {
          // Mark remaining queued files as cancelled
          setDownloadingFiles((prev) => prev.map((f) => (f.status === 'queued' ? { ...f, status: 'cancelled' } : f)));
          // Show cancel toast with completed upload count
          showCloudUploadCancelToast();
          return;
        }

        // Check if there are more files to process and we have capacity
        if (downloadState.currentIndex >= filesToProcess.length || downloadState.activeDownloads >= MAX_CONCURRENT_DOWNLOADS_LIMIT) {
          return;
        }

        const file = filesToProcess[downloadState.currentIndex];
        downloadState.currentIndex += 1;
        downloadState.activeDownloads += 1;

        // Update status to show current file is downloading
        setDownloadingFiles((prev) => prev.map((f) => {
          if (f.id === file.id) {
            return { ...f, status: 'downloading', progress: 0 };
          }
          return f;
        }));

        // Process file and when done, start next one
        processFile(file).finally(() => {
          downloadState.activeDownloads -= 1;
          // Start next file when this one completes (if not cancelled)
          if (!bulkDownloadCancelRef?.current) {
            startNextDownload();
          }
        });

        // Try to start another download if we have capacity
        startNextDownload();
      };

      // Start initial batch of downloads (up to MAX_CONCURRENT_DOWNLOADS_LIMIT)
      startNextDownload();

      // Close modal and navigate to your videos immediately (like VideoUploadModal)
      handleCloudModalClose();
      navigate(Pathname.yourVideos);

      // Call the parent callback if provided (only for videos)
      if (onCloudFilesSelected && filesToProcess.length > 0) {
        onCloudFilesSelected(filesToProcess, service);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error uploading cloud files:', error);

      setAppSnackbar({
        isVisible: true,
        message: error.message || 'Failed to upload files from cloud storage',
        type: 'error',
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Check if QuickUserUploadPage passed pending cloud files to process
  useEffect(() => {
    const pending = sessionStorage.getItem('pendingCloudFiles');
    if (pending) {
      sessionStorage.removeItem('pendingCloudFiles');
      try {
        const { files, service } = JSON.parse(pending);
        if (files && files.length > 0) {
          handleCloudFilesSelected(files, service);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[UploadDropdown] Failed to parse pending cloud files:', e);
      }
    }
  }, []); // eslint-disable-line

  // Handle photo album selection - download photos and upload to album
  const handlePhotoAlbumSelected = async (albumData) => {
    const { id: albumId, title: albumTitle } = albumData;
    const photos = pendingCloudPhotos;
    const service = pendingCloudPhotoService;

    // Close the album modal
    setPhotoAlbumModalOpen(false);

    if (photos.length === 0) {
      setPendingCloudPhotos([]);
      setPendingCloudPhotoService(null);
      return;
    }

    // eslint-disable-next-line no-console
    console.log('[CLOUD_PHOTO_UPLOAD] Starting photo upload to album:', albumId, 'photos:', photos.length);

    // Get cloud access token
    const cloudServices = localStorage.getItem('cloudConnectedServices');
    const cloudAccessToken = cloudServices ? JSON.parse(cloudServices)[service]?.accessToken : null;

    if (!cloudAccessToken) {
      setAppSnackbar({
        isVisible: true,
        message: 'Cloud service not connected',
        type: 'error',
      });
      setPendingCloudPhotos([]);
      setPendingCloudPhotoService(null);
      return;
    }

    // Show download progress box
    setDownloadBox(true);
    const initialDownloadingFiles = photos.map((file, index) => ({
      id: file.id,
      name: file.name,
      progress: 0,
      status: index < 2 ? 'downloading' : 'queued',
      source: 'cloud-photo',
    }));
    setDownloadingFiles(initialDownloadingFiles);

    // Register photo upload with upload manager
    uploadManager.createPhotoUpload({
      albumId,
      albumTitle: albumTitle || 'Photo Album',
      photoCount: photos.length,
      updateUploadPercentages,
    });

    // Initialize upload percentage
    updateUploadPercentages(albumId, `0/${photos.length}`);

    // Navigate to photo albums
    navigate(`${Pathname.yourVideos}?tab=photoalbum`);

    // Process photos: download from cloud, then batch upload to album
    const downloadedPhotos = [];
    let downloadedCount = 0;

    // Download photos with concurrency of 2
    const downloadPhoto = async (photo, index) => {
      try {
        // Update status to downloading
        setDownloadingFiles((prev) => prev.map((f) => (f.id === photo.id ? { ...f, status: 'downloading' } : f)));

        let tempUrl = null;
        let headers = {};

        if (service === 'dropbox') {
          // Get Dropbox temporary link - use ID for Dropbox
          const dropboxHeaders = {
            Authorization: `Bearer ${cloudAccessToken}`,
            'Content-Type': 'application/json',
          };

          // Add namespace header for shared folder access
          if (photo.namespaceId) {
            dropboxHeaders['Dropbox-API-Path-Root'] = JSON.stringify({
              '.tag': 'namespace_id',
              namespace_id: photo.namespaceId,
            });
          }

          const response = await AXIOS.post(
            'https://api.dropboxapi.com/2/files/get_temporary_link',
            { path: photo.id },
            { headers: dropboxHeaders },
          );
          tempUrl = response.data.link;
        } else if (service === 'googledrive') {
          // Google Drive direct download link
          tempUrl = `https://www.googleapis.com/drive/v3/files/${photo.id}?alt=media`;
          headers = {
            Authorization: `Bearer ${cloudAccessToken}`,
          };
        }

        // Download the photo
        const downloadResponse = await fetch(tempUrl, { headers });
        if (!downloadResponse.ok) {
          throw new Error(`Failed to download ${photo.name}`);
        }

        const blob = await downloadResponse.blob();

        // Mark as completed
        setDownloadingFiles((prev) => prev.map((f) => (f.id === photo.id ? { ...f, progress: 100, status: 'completed' } : f)));

        downloadedCount += 1;
        return { blob, name: photo.name, index };
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Error downloading photo ${photo.name}:`, error);
        setDownloadingFiles((prev) => prev.map((f) => (f.id === photo.id ? { ...f, status: 'error' } : f)));
        logAppEvent({
          userName: `${userDetails?.firstname || localStorage.getItem('firstname') || ''} ${userDetails?.lastname || localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
          functionAffected: 'Cloud Download',
          description: `Cloud photo download failed — file: "${photo.name}", service: ${service}`,
          severity: 'high',
          errorMessage: error?.message || 'Cloud photo download failed',
          component: 'UploadDropdown',
          userAction: 'Cloud photo download called',
        });
        return null;
      }
    };

    // Download all photos with concurrency
    const downloadQueue = [...photos];
    const activeDownloads = new Set();
    const MAX_CONCURRENT = 2;

    await new Promise((resolve) => {
      const processNext = async () => {
        while (activeDownloads.size < MAX_CONCURRENT && downloadQueue.length > 0) {
          const photo = downloadQueue.shift();
          const photoIndex = photos.indexOf(photo);
          activeDownloads.add(photo.id);

          downloadPhoto(photo, photoIndex).then((result) => {
            activeDownloads.delete(photo.id);
            if (result) {
              downloadedPhotos.push(result);
            }
            if (downloadQueue.length > 0 || activeDownloads.size > 0) {
              processNext();
            } else {
              resolve();
            }
          });
        }

        if (downloadQueue.length === 0 && activeDownloads.size === 0) {
          resolve();
        }
      };

      processNext();
    });

    // eslint-disable-next-line no-console
    console.log('[CLOUD_PHOTO_UPLOAD] Downloaded:', downloadedPhotos.length, 'photos');

    // Now upload photos in batches to the album
    if (downloadedPhotos.length > 0) {
      const batchSize = 5;
      let uploadedCount = 0;
      const getToken = () => localStorage.getItem('token');

      // Sort by original index to maintain order
      downloadedPhotos.sort((a, b) => a.index - b.index);

      // Convert first photo to base64 for album icon (always send in first batch)
      let iconBase64 = null;
      if (downloadedPhotos.length > 0) {
        const firstBlob = downloadedPhotos[0].blob;
        iconBase64 = await new Promise((resolveBase64) => {
          const reader = new FileReader();
          reader.onloadend = () => resolveBase64(reader.result);
          reader.readAsDataURL(firstBlob);
        });
      }

      // Process in batches
      for (let i = 0; i < downloadedPhotos.length; i += batchSize) {
        const batch = downloadedPhotos.slice(i, i + batchSize);
        const isFirstBatch = i === 0;

        try {
          const formData = new FormData();
          formData.append('album_id', albumId);
          formData.append('visibility', '2'); // All Viewers
          formData.append('group_id', '');
          // Send icon only in the first batch for new albums
          if (isFirstBatch && iconBase64) {
            formData.append('icon', iconBase64);
          }
          formData.append('token', getToken());

          // Append photos
          batch.forEach(({ blob, name }, idx) => {
            formData.append('photo_file[]', blob, name || `photo_${idx}.jpg`);
          });

          // eslint-disable-next-line no-await-in-loop
          await axios.post(`${baseUrl}/addPhotoContent`, formData);

          uploadedCount += batch.length;

          // Update progress
          updateUploadPercentages(albumId, `${uploadedCount}/${downloadedPhotos.length}`);
          uploadManager.updatePhotoUploadProgress(albumId, uploadedCount, downloadedPhotos.length);

          // eslint-disable-next-line no-console
          console.log(`[CLOUD_PHOTO_UPLOAD] Batch uploaded: ${uploadedCount}/${downloadedPhotos.length}`);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('[CLOUD_PHOTO_UPLOAD] Batch upload error:', error);
        }
      }

      // Complete
      updateUploadPercentages(albumId, null);
      uploadManager.completePhotoUpload(albumId);

      setAppSnackbar({
        isVisible: true,
        message: `Successfully uploaded ${uploadedCount} photos to "${albumTitle}"`,
        type: 'success',
      });
    }

    // Clean up
    setPendingCloudPhotos([]);
    setPendingCloudPhotoService(null);
  };

  const handleListKeyDown = useCallback((event) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    }
  }, []);

  // Return focus to the button when we transitioned from !open -> open
  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }
    prevOpen.current = open;
  }, [open]);

  // Per-tile loading (grid variant): tracks which tile's handler is still
  // running so we can show a spinner + highlighted border inside that box only.
  const [loadingKey, setLoadingKey] = useState(null);
  const isMountedRef = useRef(true);
  useEffect(() => () => { isMountedRef.current = false; }, []);
  const runOption = async (option, event) => {
    if (disabled || loadingKey) return;
    if (event?.persist) event.persist();
    setLoadingKey(option.key);
    try {
      await option.onClick(event);
    } catch (err) {
      console.error(`Upload option "${option.key}" failed:`, err);
    } finally {
      if (isMountedRef.current) setLoadingKey(null);
    }
  };

  // ── "Choose Local Files" tile (grid variant) ──────────────────────────────
  // Ported from /storage/videos: a single hidden file picker that accepts both
  // videos and photos, auto-splits the selection, and uploads each through the
  // app's real pipeline (global upload widget) — no metadata modal. This
  // replaces the separate "Upload Videos" / "Upload Photos" tiles.
  const filesInputRef = useRef(null);
  const photoAlbumIdRef = useRef(null);

  // Free-plan limit check — same gate as the other upload options. Returns true
  // (and shows the upgrade popup) when a free user is at the item cap.
  const isUploadLimitBlocked = async () => {
    try {
      const res = await autoSignIn();
      const currentlyUploadingCount = uploadManager.getCurrentlyUploadingCount();
      if (
        IsfreePlan
        && !res?.data?.subscription
        && res?.data?.totalContentUploaded + currentlyUploadingCount >= ALLOWED_UPLOADS_COUNT
      ) {
        setTrailPopUpModalVisible(true);
        return true;
      }
    } catch (error) {
      // Don't block the user if the check fails.
      console.error('Error checking upload limits (Choose Local Files):', error);
    }
    return false;
  };

  const handleChooseLocalFiles = async () => {
    if (await isUploadLimitBlocked()) return;
    filesInputRef.current?.click();
  };

  // Convert the placeholder image to base64 (used when no thumbnail can be made)
  const getForNoImageBase64 = async () => {
    const response = await fetch(ForNoImage);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  // Backend fallback for thumbnail generation (sends first 5MB of video)
  const callBackendThumbnailAPI = async (file) => {
    try {
      const videoFilePiece = new FormData();
      videoFilePiece.append('video_file', file.slice(0, 5 * 1024 * 1024));
      const response = await fetch('https://conf.eq7.in/generate-thumbnail/', {
        method: 'POST',
        body: videoFilePiece,
      });
      if (!response.ok) throw new Error(`Server error ${response.status}`);
      const data = await response.json();
      if (data?.thumbnail) {
        const res = await fetch(`data:image/png;base64,${data.thumbnail}`);
        const blob = await res.blob();
        return [blob];
      }
      return [];
    } catch (err) {
      console.error('Backend thumbnail generation failed:', err);
      return [];
    }
  };

  // Generate thumbnail blobs from a video file using canvas, backend fallback
  const generateThumbnailBlobsFromVideo = (file) => new Promise((resolve) => {
    const video = document.createElement('video');
    const videoUrl = URL.createObjectURL(file);
    video.src = videoUrl;
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;

    const fallbackToBackend = async () => {
      URL.revokeObjectURL(videoUrl);
      video.remove();
      const backendBlobs = await callBackendThumbnailAPI(file);
      resolve({ thumbnailBlobs: backendBlobs, videoHeight: 0, videoWidth: 0 });
    };

    video.onerror = () => { fallbackToBackend(); };

    video.onloadeddata = () => {
      const { duration } = video;
      const vHeight = video.videoHeight;
      const vWidth = video.videoWidth;
      const thumbnailCount = 6;
      const interval = duration / (thumbnailCount + 1);
      const tempBlobs = [];
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      let currentIndex = 1;

      const captureNextFrame = () => {
        if (currentIndex > thumbnailCount) {
          URL.revokeObjectURL(videoUrl);
          video.remove();
          if (tempBlobs.length === 0) {
            callBackendThumbnailAPI(file).then((backendBlobs) => {
              resolve({ thumbnailBlobs: backendBlobs, videoHeight: vHeight, videoWidth: vWidth });
            });
            return;
          }
          resolve({ thumbnailBlobs: tempBlobs, videoHeight: vHeight, videoWidth: vWidth });
          return;
        }
        video.currentTime = interval * currentIndex;
        video.onseeked = () => {
          canvas.width = vWidth;
          canvas.height = vHeight;
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => {
            if (blob) tempBlobs.push(blob);
            currentIndex += 1;
            captureNextFrame();
          }, 'image/jpeg', 0.9);
        };
      };
      captureNextFrame();
    };
  });

  // Save video metadata after the S3 upload completes (secure-upload aware)
  const addLocalVideoToAPI = async (draftID, draftState, secureUploadData = null) => {
    const formData = new FormData();
    const draftData = draftState[draftID];
    const isSecureUpload = secureUploadData && secureUploadData.s3Key;
    if (draftData) {
      formData.append('draft_id', draftID || '');
      formData.append('title', draftData.title || '');
      formData.append('category_id', draftData.category || '');
      formData.append('file_name', draftData.fileName || '');
      if (isSecureUpload) {
        formData.append('file_key', secureUploadData.s3Key);
        formData.append('document_id', secureUploadData.documentId || '');
        formData.append('bucket_folder_path', secureUploadData.s3Key);
        if (draftData.thumbnail) {
          formData.append('thumbnail', base64ToBinary(draftData.thumbnail));
        }
        if (secureUploadData.thumbnails && Array.isArray(secureUploadData.thumbnails)) {
          secureUploadData.thumbnails.forEach((thumbnail, index) => {
            if (thumbnail && index !== draftData.selectedThumbnailIndex) {
              const binary = typeof thumbnail === 'string' ? base64ToBinary(thumbnail) : thumbnail;
              formData.append('thumbnails[]', binary);
            }
          });
        }
      } else {
        if (draftData.thumbnail) {
          formData.append('thumbnail', base64ToBinary(draftData.thumbnail));
        }
        if (draftData.thumbnails && Array.isArray(draftData.thumbnails)) {
          draftData.thumbnails.forEach((binary, index) => {
            if (binary && index !== draftData.selectedThumbnailIndex) {
              formData.append('thumbnails[]', binary);
            }
          });
        }
        formData.append('uploadKey', draftData.uploadKey || '');
        formData.append('bucket_folder_path', `${draftData.s3Url}${draftData.uploadBucket}/${draftData.uploadKey}`);
        formData.append('s3_image_bucket_url', draftData.s3Url || '');
      }
      formData.append('description', draftData.description || '');
      formData.append('video_size', changeSize(draftData.videoSize) || '');
      formData.append('group_ids', (draftData.selectedGroups || []).join(','));
      formData.append('visibility', draftData.visibility || '');
      formData.append('subcategory_id', draftData.selectedSubCategory || '');
      formData.append('playlist_ids', draftData.selectedPlaylist || '');
      formData.append('videoHeight', draftData.videoHeight || '');
      formData.append('videoWidth', draftData.videoWidth || '');
      formData.append('videoOreintation', draftData.orientation || '');
      formData.append('s3_status', draftData.uploadStatus || '');
    }
    const response = await axios.post(`${baseUrl}/V6/addVideo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  };

  // Upload a single video through the app's upload manager (global progress widget)
  const startLocalVideoUpload = async (videoFile) => {
    const fileExtension = videoFile.name.split('.').pop().toLowerCase();
    const number = Math.floor(Math.random() * 10000) + 1;
    const fileName = `${Date.now()}-${userId}-video-${number}.${fileExtension}`;
    const bucketName = getBucketName(userId);
    const s3Url = generateS3Url();
    const draftID = uuid();
    const videoSize = videoFile.size;

    const { thumbnailBlobs, videoHeight, videoWidth } = await generateThumbnailBlobsFromVideo(videoFile);
    let thumbnailBase64;
    let thumbnailsBinary;
    if (thumbnailBlobs.length > 0) {
      thumbnailBase64 = await blobToBase64(thumbnailBlobs[0]);
      thumbnailsBinary = await Promise.all(
        thumbnailBlobs.map((blob) => blobToBase64(blob).then((b64) => base64ToBinary(b64)))
      );
    } else {
      thumbnailBase64 = await getForNoImageBase64();
      thumbnailsBinary = [base64ToBinary(thumbnailBase64)];
    }

    uploadLargeFile({
      userId,
      bucket: bucketName,
      key: fileName,
      file: videoFile,
      draftId: draftID,
      thumbnails: thumbnailsBinary,
      uploadPercentages,
      updateUploadPercentages,
      setUploadContainer,
      setUploadContainer2,
      setIamUploading,
      dummyData: {
        title: videoFile.name.replace(/\.[^/.]+$/, ''),
        fileName: videoFile.name,
        thumbnail: thumbnailBase64,
        thumbnails: thumbnailsBinary,
        ForNoImage,
        category: 'All Videos',
        description: '',
        videoSize,
        selectedGroups: [],
        changeSize,
      },
      updateDummy,
      setDraftCategoryState,
      onSuccess: async (draftId, secureUploadData = null) => {
        const isSecureUpload = secureUploadData && secureUploadData.s3Key;
        const updatedDraftData = {
          title: videoFile.name.replace(/\.[^/.]+$/, ''),
          category: '',
          fileName: videoFile.name,
          thumbnail: thumbnailBase64,
          thumbnails: thumbnailsBinary,
          selectedThumbnailIndex: 0,
          description: '',
          videoSize,
          selectedGroups: [],
          visibility: '2',
          selectedSubCategory: '',
          selectedPlaylist: '',
          videoHeight,
          videoWidth,
          orientation: videoHeight < videoWidth ? 1 : 2,
          uploadStatus: 'completed',
          uploadKey: isSecureUpload ? secureUploadData.s3Key : fileName,
          uploadBucket: isSecureUpload ? '' : bucketName,
          s3Url: isSecureUpload ? '' : s3Url,
        };
        setDraftCategoryState((prevState) => ({ ...prevState, [draftId]: updatedDraftData }));
        try {
          await addLocalVideoToAPI(draftId, { [draftId]: updatedDraftData }, secureUploadData);
        } catch (error) {
          console.error('Failed to add video to API:', error);
        }
      },
      onError: (error, draftId) => {
        console.error('Upload failed for draft:', draftId, error);
      },
      updateUploadDetails,
    });
  };

  // Upload photos into an "All Photos" album through the app's pipeline
  const startLocalPhotoAlbumUpload = async (imageFiles) => {
    const getToken = () => localStorage.getItem('token');
    let albumId = photoAlbumIdRef.current;
    if (!albumId) {
      try {
        const albumListResponse = await axios.post(`${baseUrl}/getAlbumList`, {});
        const existingAlbums = albumListResponse.data?.data || [];
        const allPhotosAlbum = existingAlbums.find((a) => a.title === 'All Photos');
        if (allPhotosAlbum) albumId = allPhotosAlbum.album_id || allPhotosAlbum.id;
      } catch (err) {
        console.warn('Failed to fetch album list, will create new album:', err);
      }
      if (!albumId) {
        const response = await axios.post(`${baseUrl}/addEditAlbum`, {
          title: 'All Photos',
          description: '',
          visibility: '2',
          group_id: '',
          token: getToken(),
        });
        albumId = response.data?.album_id;
        if (!albumId) {
          console.error('Failed to create album');
          return;
        }
      }
      photoAlbumIdRef.current = albumId;
    }

    const totalPhotos = imageFiles.length;
    const iconBase64 = await blobToBase64(imageFiles[0]);

    uploadManager.createPhotoUpload({
      albumId,
      albumTitle: 'Photo Album',
      photoCount: totalPhotos,
      updateUploadPercentages,
    });
    updateUploadPercentages(albumId, `0/${totalPhotos}`);

    let successfulUploadCount = 0;
    // eslint-disable-next-line no-restricted-syntax
    for (let i = 0; i < imageFiles.length; i += 1) {
      try {
        const formData = new FormData();
        formData.append('album_id', albumId);
        formData.append('visibility', '2');
        formData.append('group_id', '');
        if (i === 0 && iconBase64) formData.append('icon', iconBase64);
        formData.append('token', getToken());
        formData.append('photo_file[]', imageFiles[i], imageFiles[i].name);
        // eslint-disable-next-line no-await-in-loop
        await axios.post(`${baseUrl}/addPhotoContent`, formData);
        successfulUploadCount += 1;
        updateUploadPercentages(albumId, `${successfulUploadCount}/${totalPhotos}`);
        uploadManager.updatePhotoUploadProgress(albumId, successfulUploadCount, totalPhotos);
      } catch (error) {
        console.error(`Error uploading photo ${i + 1}:`, error);
      }
    }

    updateUploadPercentages(albumId, null);
    uploadManager.completePhotoUpload(albumId);
    setAppSnackbar({
      isVisible: true,
      type: 'success',
      message: `Uploaded ${successfulUploadCount} ${successfulUploadCount === 1 ? 'photo' : 'photos'}`,
    });
  };

  // Single "Choose Local Files" picker → auto-split videos vs photos → pipeline
  const handleLocalFileSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    const inputElement = event.target;
    inputElement.value = ''; // allow re-selecting the same files
    if (files.length === 0) return;

    const videoFiles = [];
    const imageFiles = [];
    files.forEach((file) => {
      const ext = `.${file.name.split('.').pop().toLowerCase()}`;
      if (VIDEO_EXTENSIONS.includes(ext)) videoFiles.push(file);
      else if (IMAGE_EXTENSIONS.includes(ext)) imageFiles.push(file);
    });

    if (videoFiles.length === 0 && imageFiles.length === 0) {
      setAppSnackbar({
        isVisible: true,
        type: 'error',
        message: 'Unsupported file type. Please choose videos or photos.',
      });
      return;
    }

    // Per-upload photo cap. The extras are dropped and reported in a popup —
    // the album can be topped up by picking files again once this batch is in.
    let photosToUpload = imageFiles;
    if (imageFiles.length > MAX_PHOTOS_PER_UPLOAD) {
      photosToUpload = imageFiles.slice(0, MAX_PHOTOS_PER_UPLOAD);
      setPhotoCapNotice({ attempted: imageFiles.length, added: MAX_PHOTOS_PER_UPLOAD });
    }

    videoFiles.forEach((videoFile) => { startLocalVideoUpload(videoFile); });
    if (photosToUpload.length > 0) startLocalPhotoAlbumUpload(photosToUpload);
  };

  // Grid-variant tiles — one merged "Choose Local Files" tile (replaces the
  // separate Upload Videos / Upload Photos tiles) plus the remaining sources,
  // laid out and styled to match the /storage/videos uploader tab.
  const localFilesOption = { key: 'local', label: 'Choose Local Files', renderIcon: () => <LocalFilesGridIcon />, onClick: handleChooseLocalFiles };
  const storyOption = { key: 'story', label: 'Tell Your Story', renderIcon: () => <StoryGridIcon />, onClick: handleRecordYourself };
  const phoneOption = { key: 'phone', label: 'Upload From Mobile', renderIcon: () => <MobileGridIcon />, onClick: openMobileUploadGuarded };
  const appleOption = { key: 'applephotos', label: 'Apple Photos', renderIcon: () => <ApplePhotosGridIcon />, onClick: handleApplePhotos };
  const googlePhotosOption = { key: 'googlephotos', label: 'Google Photos', renderIcon: () => <GooglePhotosGridIcon />, onClick: handleCloudUpload('googledrive') };
  const dropboxOption = { key: 'dropbox', label: 'Dropbox', renderIcon: () => <DropboxGridIcon />, onClick: handleCloudUpload('dropbox') };
  const gdriveOption = { key: 'gdrive', label: 'Google Drive', renderIcon: () => <GoogleDriveGridIcon />, onClick: handleCloudUpload('googledrive') };

  const renderGridTile = (option, compact, delayClass) => (
    <button
      key={option.key}
      type="button"
      className={`${styles.uploadOptionBox} ${compact ? styles.uploadOptionBoxCompact : ''} ${styles.optionSlideIn} ${delayClass} ${disabled ? styles.disabled : ''} ${loadingKey === option.key ? styles.uploadOptionBoxLoading : ''}`}
      onClick={(event) => runOption(option, event)}
      disabled={disabled || Boolean(loadingKey)}
    >
      <span className={styles.uploadOptionIcon}>{option.renderIcon()}</span>
      <span className={styles.uploadOptionText}>{option.label}</span>
      {loadingKey === option.key && (
        <span className={styles.uploadOptionLoaderOverlay}>
          <CircularProgress size={26} style={{ color: '#5aa5ef' }} />
        </span>
      )}
    </button>
  );

  // The cloud / mobile / photo-album modals are shared across all variants.
  const modals = (
    <>
      <CloudUploadModal
        open={cloudModalOpen}
        onClose={handleCloudModalClose}
        service={selectedCloudService}
        onFilesSelected={handleCloudFilesSelected}
        isUploading={isUploading}
        onShowMessage={(message) => setAppSnackbar({
          isVisible: true,
          message,
          type: 'default',
        })}
      />

      <CloudPhotoAlbumModal
        open={photoAlbumModalOpen}
        onClose={() => {
          setPhotoAlbumModalOpen(false);
          setPendingCloudPhotos([]);
          setPendingCloudPhotoService(null);
        }}
        onAlbumSelected={handlePhotoAlbumSelected}
        photoCount={pendingCloudPhotos.length}
      />

      <MobileUploadQRModal
        open={mobileUploadModalOpen}
        onClose={() => setMobileUploadModalOpen(false)}
      />

      <UploadInProgressModal
        theme="light"
        open={uploadInProgressOpen}
        onClose={() => setUploadInProgressOpen(false)}
      />
      {/* Per-upload photo cap popup for the local-files tile (the modal-less
          photo path — PhotoUploadModal raises its own, sharing this styling). */}
      <Dialog
        open={Boolean(photoCapNotice)}
        onClose={() => setPhotoCapNotice(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: photoModalStyles.capDialogPaper }}
      >
        <div className={photoModalStyles.capDialog}>
          <span className={photoModalStyles.capIconWrap}>
            <PhotoLibrary />
          </span>
          <h2 className={photoModalStyles.capTitle}>Photo limit reached</h2>
          <p className={photoModalStyles.capMessage}>
            {`You can't choose more than ${MAX_PHOTOS_PER_UPLOAD} photos in a single upload, so we started uploading the first ${photoCapNotice?.added || 0} of the ${photoCapNotice?.attempted || 0} you picked. You can add the other ${(photoCapNotice?.attempted || 0) - (photoCapNotice?.added || 0)} to the same album once this upload finishes.`}
          </p>
          <div className={photoModalStyles.capActions}>
            <button
              type="button"
              className={photoModalStyles.capButton}
              onClick={() => setPhotoCapNotice(null)}
            >
              Got it
            </button>
          </div>
        </div>
      </Dialog>
      <UploadLinkModal
        open={uploadLinkModalOpen}
        onClose={() => setUploadLinkModalOpen(false)}
        onSuccess={() => {
          setAppSnackbar({
            isVisible: true,
            message: 'Shared folder created and link copied to clipboard!',
            type: 'success',
          });
        }}
      />
    </>
  );

  if (variant === 'grid') {
    return (
      <div className={`${styles.gridContainer} ${className}`}>
        {/* Hidden picker for the merged "Choose Local Files" tile */}
        <input
          ref={filesInputRef}
          type="file"
          accept={FILE_ACCEPT}
          multiple
          style={{ display: 'none' }}
          onChange={handleLocalFileSelect}
        />

        <div className={styles.uploadOptionsGrid}>
          {/* ── Row 1 ── */}
          <div className={styles.uploadOptionsRow}>
            {renderGridTile(localFilesOption, false, styles.optionDelay1)}
            {renderGridTile(phoneOption, false, styles.optionDelay2)}
            {renderGridTile(storyOption, false, styles.optionDelay3)}
          </div>

          {/* ── Row 2 ── */}
          <div className={`${styles.uploadOptionsRow} ${styles.uploadOptionsRowCompact}`}>
            {renderGridTile(appleOption, true, styles.optionDelay4)}
            {renderGridTile(googlePhotosOption, true, styles.optionDelay3)}
          </div>

          {/* ── Row 3 (cloud sources) ── */}
          {showCloudOptions && (
            <div className={`${styles.uploadOptionsRow} ${styles.uploadOptionsRowCompact}`}>
              {renderGridTile(dropboxOption, true, styles.optionDelay4)}
              {renderGridTile(gdriveOption, true, styles.optionDelay4)}
            </div>
          )}
        </div>
        {modals}
      </div>
    );
  }

  if (isCompact) {
    return (
      <div className={styles.compactContainer}>
        <button
          ref={anchorRef}
          className={`${styles.compactButton} ${className} ${disabled ? styles.disabled : ''}`}
          onClick={handleToggle}
          disabled={disabled}
          aria-controls={open ? 'composition-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="true"
        >
          <VideoUploadIcon className={styles.uploadIcon} />
          {isLoading && <CircularProgress size={14} style={{ marginLeft: '4px', color: '#fff' }} />}
          <ArrowDropDown className={styles.dropdownIcon} />
        </button>
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          placement="bottom-start"
          transition
          className={styles.popper}
        >
          <Paper className={styles.paper}>
            <ClickAwayListener onClickAway={handleClose}>
              <MenuList
                autoFocusItem={open}
                id="composition-menu"
                aria-labelledby="composition-button"
                onKeyDown={handleListKeyDown}
                className={styles.menuList}
              >
                <MenuItem onClick={handleVideoUpload} className={styles.menuItem}>
                  <VideoLibrary className={styles.menuIcon} />
                  <span className={styles.menuText}>Upload Videos</span>
                </MenuItem>
                <MenuItem onClick={handlePhotoUpload} className={styles.menuItem}>
                  <PhotoLibrary className={styles.menuIcon} />
                  <span className={styles.menuText}>Upload Photos</span>
                </MenuItem>
                <MenuItem onClick={handleRecordYourself} className={styles.menuItem}>
                  <Videocam className={styles.menuIcon} />
                  <span className={styles.menuText}>Tell Your Story</span>
                </MenuItem>
              </MenuList>
            </ClickAwayListener>
          </Paper>
        </Popper>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <button
        ref={anchorRef}
        className={`${styles.uploadButton} ${className} ${disabled ? styles.disabled : ''}`}
        onClick={handleToggle}
        disabled={disabled}
        aria-controls={open ? 'composition-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
      >
        {/* <VideoUploadIcon className={styles.uploadIcon} /> */}
        <span className={styles.buttonText}>Upload</span>
        {isLoading && <CircularProgress size={16} style={{ marginLeft: '8px', color: '#fff' }} />}
        {/* <ArrowDropDown className={styles.dropdownIcon} /> */}
      </button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-start"
        transition
        className={styles.popper}
      >
        <Paper className={styles.paper}>
          <ClickAwayListener onClickAway={handleClose}>
            <MenuList
              autoFocusItem={open}
              id="composition-menu"
              aria-labelledby="composition-button"
              onKeyDown={handleListKeyDown}
              className={styles.menuList}
            >
              <MenuItem onClick={handleVideoUpload} className={styles.menuItem}>
                <VideoLibrary className={styles.menuIcon} />
                <span className={styles.menuText}>Upload Videos</span>
              </MenuItem>
              <MenuItem onClick={handlePhotoUpload} className={styles.menuItem}>
                <PhotoLibrary className={styles.menuIcon} />
                <span className={styles.menuText}>Upload Photos</span>
              </MenuItem>
              <MenuItem onClick={handleRecordYourself} className={styles.menuItem}>
                <Videocam className={styles.menuIcon} />
                <span className={styles.menuText}>Tell Your Story</span>
              </MenuItem>
              <MenuItem
                onClick={openMobileUploadGuarded}
                className={styles.menuItem}
              >
                <PhoneAndroid className={styles.menuIcon} />
                <span className={styles.menuText}>Upload From Phone</span>
              </MenuItem>
              <MenuItem onClick={handleApplePhotos} className={styles.menuItem}>
                <ApplePhotosGlyph className={styles.menuIcon} />
                <span className={styles.menuText}>Apple Photos</span>
              </MenuItem>
              {/* No dedicated Google Photos integration — route to the Google
                  Drive cloud connect (same mapping as the uploader UI). */}
              <MenuItem onClick={handleCloudUpload('googledrive')} className={styles.menuItem}>
                <GooglePhotosGlyph className={styles.menuIcon} />
                <span className={styles.menuText}>Google Photos</span>
              </MenuItem>
              {SHARED_FEATURE_ENABLED && (
                <MenuItem onClick={handleUploadViaLink} className={styles.menuItem}>
                  <svg
                    className={styles.menuIcon}
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10.59 13.41c.41.39.41 1.03 0 1.42-.39.39-1.03.39-1.42 0a5.003 5.003 0 0 1 0-7.07l3.54-3.54a5.003 5.003 0 0 1 7.07 0 5.003 5.003 0 0 1 0 7.07l-1.49 1.49c.01-.82-.12-1.64-.4-2.42l.47-.48a2.982 2.982 0 0 0 0-4.24 2.982 2.982 0 0 0-4.24 0l-3.53 3.53a2.982 2.982 0 0 0 0 4.24zm2.82-4.24c.39-.39 1.03-.39 1.42 0a5.003 5.003 0 0 1 0 7.07l-3.54 3.54a5.003 5.003 0 0 1-7.07 0 5.003 5.003 0 0 1 0-7.07l1.49-1.49c-.01.82.12 1.64.4 2.43l-.47.47a2.982 2.982 0 0 0 0 4.24 2.982 2.982 0 0 0 4.24 0l3.53-3.53a2.982 2.982 0 0 0 0-4.24.973.973 0 0 1 0-1.42z"
                      fill="currentColor"
                    />
                  </svg>
                  <span className={styles.menuText}>Shared Folders</span>
                </MenuItem>
              )}
              {showCloudOptions && (
                <>
                  <MenuItem onClick={handleCloudUpload('dropbox')} className={styles.menuItem}>
                    <DropboxGlyph className={styles.menuIcon} />
                    <span className={styles.menuText}>From Dropbox</span>
                  </MenuItem>
                  <MenuItem onClick={handleCloudUpload('googledrive')} className={styles.menuItem}>
                    <GoogleDriveGlyph className={styles.menuIcon} />
                    <span className={styles.menuText}>From Google Drive</span>
                  </MenuItem>
                </>
              )}
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </Popper>

      {modals}
    </div>
  );
};

export default UploadDropdown;
